import { formatDate } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";
import { AlertRecord } from "@models/alerts/alert-record";
import {
  alertStateFilterOptions,
  AlertStatus,
  AlertType,
  alertTypesFilterOptions,
  ResolvedStatus
} from "@models/alerts/alerts-enums";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { FilterOption } from "@models/grid/grid-filter";
import { PageResult } from "@models/requests/page-result.interface";
import { AlertsPageConfigKeyEnum, IMapAlertRequestFn } from "@pages/alerts/components/alerts-page/alerts-page-configs";
import {
  AgAlertsCategoryComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-category/ag-alerts-category.component";
import {
  AgAlertsResolvedStatusComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-resolved-status/ag-alerts-resolved-status.component";
import {
  AgAlertsStatusComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-status/ag-alerts-status.component";
import {
  AgAlertsTitleComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-title/ag-alerts-title.component";
import {
  AgYesNoStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-yes-no-status/ag-yes-no-status.component";
import { AlertsApiService } from "@shared/services/alerts-api.service";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { dateTimeComparator } from "@shared/utils/grid/date-time-comparator";
import { uniqueArrayToFilterOptionHelper } from "@shared/utils/unique-array-to-filter-option.helper";
import { ColDef, IServerSideDatasource, IServerSideGetRowsParams, ValueFormatterParams } from "ag-grid-community";
import { Subject } from "rxjs";

@Injectable()
export class AlertsGridService extends BaseGridService {
  private alertsApiService = inject(AlertsApiService);

  totalRecords = signal<number>(0);
  dataSource = signal<IServerSideDatasource>(this.getDataSource());

  dataLoaded$ = new Subject<PageResult<AlertRecord>>();

  mapRequestFn: IMapAlertRequestFn | null = null;

  // filters
  readonly alertTypeFilterOptions: FilterOption[] = alertTypesFilterOptions;
  readonly alertStateFilterOptions: FilterOption[] = alertStateFilterOptions;
  readonly alertStatusFilterOptions: FilterOption[] = convertEnumToFilterOption(ResolvedStatus);
  readonly requireAttentionFilterOptions: FilterOption[] = [
    { label: 'Yes', value: 'true', active: false },
    { label: 'No', value: 'false', active: false }
  ];

  profileUserNameList: string[] = [];
  companyAccountManagerFilterOptions: FilterOption[] = [];
  companyPartnerManagerFilterOptions: FilterOption[] = [];

  initMapRequestFn(mapRequestFn: IMapAlertRequestFn): void {
    this.mapRequestFn = mapRequestFn;
  }

  setGridFilterOptions(profileUserNameList: string[]): void {
    this.profileUserNameList = profileUserNameList;
    this.companyAccountManagerFilterOptions = uniqueArrayToFilterOptionHelper(this.profileUserNameList);
    this.companyPartnerManagerFilterOptions = uniqueArrayToFilterOptionHelper(this.profileUserNameList);
  }

  setDataSource(searchText?: string, endRow?: number): void {
    this.dataSource.set(this.getDataSource(searchText, endRow));
  }

  getDataSource(searchText?: string, endRow?: number): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const request = this.mapRequestFn?.(params) ?? params.request;

        this.alertsApiService.getInboxAlertList(request, searchText)
          .subscribe({
            next: (result) => {
              this.totalRecords.set(result.total);

              params.success({
                rowData: result.records,
                rowCount: result.total,
              });

              this.dataLoaded$.next(result);
            },
            error: (err) => {
              console.error(err);
              params.fail();
            },
          });
      },
    };
  }

  setColDefsWithKey(actionColumn: ColDef, key: AlertsPageConfigKeyEnum, openAlertFn: Function): void {
    const colDefs: ColDef[] = [
      {
        headerName: 'Date',
        field: 'createdOn',
        sort: 'desc',
        flex: 1.1,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateTimeComparator,
        },
        valueFormatter: (params: ValueFormatterParams<AlertRecord, Date>) => {
          return formatDate(params.value ?? '', 'dd MMM yyyy HH:mm', 'en-US');
        },
        cellDataType: CellDataType.DATE,
        suppressMovable: true,
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Description',
        field: 'modelDetailTitle',
        flex: 2,
        autoHeight: true,
        cellRenderer: AgAlertsTitleComponent,
        cellRendererParams: {
          openAlert: openAlertFn,
        },
        cellClass: 'hover-underline',
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Type of Alert',
        field: 'type',
        flex: 1,
        cellRenderer: AgAlertsCategoryComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.alertTypeFilterOptions,
          values: Object.values(AlertType).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'State',
        field: 'status',
        flex: 0.7,
        cellRenderer: AgAlertsStatusComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.alertStateFilterOptions,
          values: [
            'Read',
            ...Object.values(AlertStatus).filter((v) => !isNaN(Number(v)))
          ],
        },
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Status',
        field: 'resolvedStatus',
        flex: 0.7,
        cellRenderer: AgAlertsResolvedStatusComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.alertStatusFilterOptions,
          values: Object.values(ResolvedStatus).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Entity Name',
        field: 'entityName',
        flex: 0.5,
        hide: true,
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Require Attention',
        field: 'needAttention',
        flex: 0.5,
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.requireAttentionFilterOptions,
          values: [true, false]
        },
        cellRenderer: AgYesNoStatusComponent,
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Is Read',
        field: 'isRead',
        hide: true,
        initialHide: true,
        lockVisible: false,
        suppressColumnsToolPanel: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.requireAttentionFilterOptions,
          values: [true, false]
        },
        cellRenderer: AgYesNoStatusComponent,
      },
      actionColumn
    ]
      .filter((colDef) => {
        const field = colDef.field;

        if (field == 'needAttention' && key == AlertsPageConfigKeyEnum.Required) {
          return false;
        }

        return true;
      })
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));

    this.colDefs.set(colDefs);
  }
}
